/**
 * Flight Log Modal Component
 * Tabbed modal for logging/editing flights.
 *
 * Six tabs:
 *   1. Flight       — date, aircraft, pilot, captain, airports, times
 *   2. Fuel & Oil   — tank levels, oil reading, fuel account
 *   3. Defects      — defect reports
 *   4. Costs        — reimbursable expenses
 *   5. Passengers   — passenger list
 *   6. Notes        — free-text comments
 *
 * Mobile-first layout. All panels share a fixed min-height to prevent
 * the modal from jumping when switching tabs.
 *
 * @typedef {Object} FlightLogModalConfig
 * @property {Object} [flight] - Existing flight record (edit / complete-booking mode)
 * @property {Object} [fuelOil] - Existing fuel/oil record for edit mode
 * @property {Object[]} [defects] - Existing linked defects for edit mode
 * @property {Object[]} [expenses] - Existing expenses for edit mode
 * @property {Function} onSaved - () => void — called after successful save
 */

import { openDetailModal } from './detail-modal.js';
import { createAirportComboBox } from './airport-combo-box.js';
import { createExpenseList } from './expense-list.js';
import { createPassengerList } from './passenger-list.js';
import { createDefectList } from './defect-list.js';
import { createFuelAccountForm } from './fuel-account-form.js';
import { getAircraft, getAirports, getPilots, getPilotAliases, createAirport } from '../services/api.js';
import { logFlightWithFuelAndDefects } from '../services/transactions.js';
import { getUser } from '../services/auth.js';
import { hmmToMins, minsToHMM, minsToDecimal } from '../utils/format.js';

// ── Exported pure helpers (for testing) ──────────────────

// ── Shared helpers ────────────────────────────────────

/**
 * Oil-related tank name patterns (case-insensitive).
 * Tanks matching these are NOT fuel tanks and should not count
 * when deciding whether an aircraft has fuel tanks configured.
 */
const OIL_TANK_RE = /^oil$/i;

/**
 * Return only the fuel tanks from a fuel_tank_config, excluding oil tanks.
 * @param {Object} fuelTankConfig - { tanks: [...] }
 * @returns {Object[]} Fuel-only tank entries
 */
export function getFuelTanks(fuelTankConfig) {
  const tanks = fuelTankConfig?.tanks;
  if (!Array.isArray(tanks)) return [];
  return tanks.filter(t => !OIL_TANK_RE.test((t.name || '').trim()));
}

/**
 * Build fuel/oil reading fields from the new tank config format.
 * Config shape: { tanks: [ { name, capacity, unit }, ... ] }
 *
 * Each tank gets a numeric input showing "Name (max capacity unit)".
 * @param {Object} fuelTankConfig - Aircraft fuel_tank_config
 * @param {Object} [existingFuelOil] - Existing readings keyed by tank index
 * @returns {HTMLElement}
 */
export function buildFuelFields(fuelTankConfig, _oilUnit, existingFuelOil = null) {
  const container = document.createElement('div');
  container.className = 'flight-log-modal__fuel-section';

  const grid = document.createElement('div');
  grid.className = 'form-grid';

  const tanks = fuelTankConfig?.tanks;
  const fuelTanks = getFuelTanks(fuelTankConfig);
  if (Array.isArray(tanks) && tanks.length > 0 && fuelTanks.length > 0) {
    tanks.forEach((tank, i) => {
      const fieldDiv = document.createElement('div');
      fieldDiv.className = 'form-grid__field';

      const label = document.createElement('label');
      label.setAttribute('for', `tank-${i}`);
      const capStr = tank.capacity ? ` (max ${tank.capacity} ${tank.unit || ''})` : '';
      label.textContent = `${tank.name}${capStr}`;
      fieldDiv.appendChild(label);

      const input = document.createElement('input');
      input.type = 'number';
      input.id = `tank-${i}`;
      input.name = `tank-${i}`;
      input.className = 'flight-log-modal__fuel-input';
      input.setAttribute('data-tank-index', String(i));
      input.setAttribute('data-tank-name', tank.name);
      input.placeholder = '0';
      input.min = '0';
      input.step = '0.1';
      if (tank.capacity) input.max = String(tank.capacity);

      // Clamp value on input — prevent negatives and values above capacity
      input.addEventListener('input', () => {
        const val = parseFloat(input.value);
        if (Number.isNaN(val)) return;
        if (val < 0) input.value = '0';
        else if (tank.capacity && val > tank.capacity) input.value = String(tank.capacity);
      });

      // Pre-populate from existing readings (check meta.tank_readings first,
      // then fall back to top-level keys for backward compat with older records)
      if (existingFuelOil) {
        const tr = existingFuelOil.meta?.tank_readings;
        const reading = tr?.[`tank_${i}`] ?? tr?.[tank.name]
          ?? existingFuelOil[`tank_${i}`] ?? existingFuelOil[tank.name];
        if (reading != null) input.value = String(reading);
      }

      fieldDiv.appendChild(input);
      grid.appendChild(fieldDiv);
    });
  } else {
    // No tanks configured — show a warning instead of input fields.
    // The onSave handler will block submission for unconfigured aircraft.
    const note = document.createElement('p');
    note.className = 'text-muted flight-log-modal__no-tanks-warning';
    note.textContent = 'This aircraft has no fuel tanks configured (only oil). An admin must add fuel tank details in Aircraft settings before a flight can be logged.';
    container.appendChild(note);
    return container;
  }

  container.appendChild(grid);
  return container;
}

/**
 * Build the defects section with add/remove capability.
 */
export function buildDefectsSection(existingDefects = []) {
  const container = document.createElement('div');
  container.className = 'flight-log-modal__defects-section';

  const defectsList = document.createElement('div');
  defectsList.className = 'flight-log-modal__defects-list';
  container.appendChild(defectsList);

  function addDefectField(description = '') {
    const row = document.createElement('div');
    row.className = 'flight-log-modal__defect-row';

    const input = document.createElement('textarea');
    input.className = 'flight-log-modal__defect-input';
    input.name = 'defect-description';
    input.placeholder = 'Describe the defect...';
    input.rows = 2;
    input.value = description;
    row.appendChild(input);

    const removeBtn = document.createElement('button');
    removeBtn.type = 'button';
    removeBtn.className = 'btn btn--danger btn--sm';
    removeBtn.textContent = 'Remove';
    removeBtn.addEventListener('click', () => row.remove());
    row.appendChild(removeBtn);

    defectsList.appendChild(row);
  }

  if (existingDefects && existingDefects.length > 0) {
    for (const defect of existingDefects) {
      addDefectField(defect.description || '');
    }
  }

  const addBtn = document.createElement('button');
  addBtn.type = 'button';
  addBtn.className = 'btn flight-log-modal__defect-add';
  addBtn.textContent = '+ Add Defect';
  addBtn.addEventListener('click', () => addDefectField());
  container.appendChild(addBtn);

  return container;
}

/**
 * Pre-populate flight data into form values object.
 */
export function prepopulateFlightData(flight) {
  if (!flight) return {};
  return {
    'flight-date': flight.flight_date || '',
    'flight-aircraft': flight.aircraft_id || '',
    'flight-pilot': flight.pilot_id || '',
    'flight-captain': flight.captain_name || '',
    'flight-dep-time': (flight.departure_time || flight.start_time || '').slice(0, 5),
    'flight-arr-time': (flight.arrival_time || flight.end_time || '').slice(0, 5),
    'flight-block': flight.block_time_mins ? minsToHMM(flight.block_time_mins) : '',
    'flight-time': flight.flight_time_mins ? minsToHMM(flight.flight_time_mins) : '',
    'flight-ifr': flight.ifr_time_mins ? minsToHMM(flight.ifr_time_mins) : '',
    'flight-takeoffs': String(flight.takeoffs ?? 1),
    'flight-landings': String(flight.landings ?? 1),
    'flight-who-pays': flight.who_pays || 'CAPTAIN',
  };
}

// ── Tab builder helper ───────────────────────────────────

function buildTabs(tabs) {
  const wrapper = document.createElement('div');
  wrapper.className = 'flm-tabs';

  const bar = document.createElement('div');
  bar.className = 'flm-tabs__bar';

  const panels = document.createElement('div');
  panels.className = 'flm-tabs__panels';

  const buttons = [];

  tabs.forEach((tab, i) => {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'flm-tabs__btn' + (i === 0 ? ' flm-tabs__btn--active' : '');
    btn.textContent = tab.label;
    btn.setAttribute('data-tab', tab.id);
    bar.appendChild(btn);
    buttons.push(btn);

    const panel = document.createElement('div');
    panel.className = 'flm-tabs__panel' + (i === 0 ? ' flm-tabs__panel--active' : '');
    panel.setAttribute('data-tab-panel', tab.id);
    panel.appendChild(tab.content);
    panels.appendChild(panel);
  });

  function switchTo(id) {
    buttons.forEach(b => b.classList.toggle('flm-tabs__btn--active', b.getAttribute('data-tab') === id));
    panels.querySelectorAll('.flm-tabs__panel').forEach(p =>
      p.classList.toggle('flm-tabs__panel--active', p.getAttribute('data-tab-panel') === id),
    );
  }

  bar.addEventListener('click', (e) => {
    const btn = e.target.closest('.flm-tabs__btn');
    if (btn) switchTo(btn.getAttribute('data-tab'));
  });

  wrapper.appendChild(bar);
  wrapper.appendChild(panels);
  return { wrapper, switchTo };
}

// ── Main entry point ─────────────────────────────────────

export async function openFlightLogModal(config = {}) {
  const { flight = null, fuelOil = null, defects = [], expenses = [], onSaved } = config;
  const user = getUser();
  const isEdit = !!flight;

  // Load reference data
  let aircraftList = [];
  let airportsList = [];
  let pilotsList = [];
  try {
    [aircraftList, airportsList, pilotsList] = await Promise.all([getAircraft(), getAirports(), getPilots()]);
  } catch (err) {
    console.error('Failed to load data for flight log modal:', err);
  }

  const activeAircraft = aircraftList.filter(a => {
    const retired = (a.meta && a.meta.retired) || (a.name && a.name.includes('[RETIRED]'));
    if (retired && isEdit && flight.aircraft_id === a.id) return true;
    return !retired;
  });

  const aircraftOptions = activeAircraft.map(a => ({
    value: a.id,
    label: `${a.registration} - ${a.type || a.name || ''}`,
  }));

  const pilotOptions = pilotsList.map(p => ({ value: p.id, label: p.name }));

  // Load aliases for the current user (for "Log as" dropdown)
  let userAliases = [];
  try {
    if (user?.id) {
      userAliases = await getPilotAliases(user.id);
    }
  } catch (err) {
    console.warn('Failed to load pilot aliases:', err);
  }

  // Track selected alias for "Log as" feature
  let selectedAliasId = null;

  // Read last-used airports from sessionStorage for new flights
  const lastDep = !isEdit ? (sessionStorage.getItem('lastDeparture') || '') : '';
  const lastDest = !isEdit ? (sessionStorage.getItem('lastDestination') || '') : '';

  let selectedDeparture = flight?.departure || lastDep;
  let selectedDestination = flight?.destination || lastDest;

  // ── Form fields (above the tabs) ──────────────────────

  const fields = [
    { name: 'flight-date', label: 'Date', type: 'date', required: true },
    { name: 'flight-aircraft', label: 'Aircraft', type: 'select', required: true, options: aircraftOptions },
    { name: 'flight-pilot', label: 'Captain (PIC)', type: 'select', required: true, options: pilotOptions },
    { name: 'flight-captain', label: 'P2', type: 'text', required: false, placeholder: 'P2 name' },
    { name: 'flight-dep-time', label: 'Departure Time', type: 'time', required: true },
    { name: 'flight-arr-time', label: 'Arrival Time', type: 'time', required: true },
    { name: 'flight-time', label: 'Flight Time', type: 'text', required: true, placeholder: '1:15 or 1.25' },
    { name: 'flight-block', label: 'Block Time', type: 'text', required: true, placeholder: '1:30 or 1.5' },
    { name: 'flight-ifr', label: 'IFR Time', type: 'text', placeholder: '0:00 or 0.0' },
    { name: 'flight-takeoffs', label: 'Takeoffs', type: 'number', placeholder: '1' },
    { name: 'flight-landings', label: 'Landings', type: 'number', placeholder: '1' },
    { name: 'flight-who-pays', label: 'Who Pays', type: 'select', options: [
      { value: 'CAPTAIN', label: 'Captain' },
      { value: 'P2', label: 'P2' },
    ] },
  ];

  const values = isEdit ? prepopulateFlightData(flight) : {
    'flight-date': new Date().toISOString().slice(0, 10),
    'flight-pilot': getUser()?.id || '',
    'flight-takeoffs': '1',
    'flight-landings': '1',
    'flight-who-pays': 'CAPTAIN',
  };

  let currentAircraft = null;
  if (isEdit && flight.aircraft_id) {
    currentAircraft = aircraftList.find(a => a.id === flight.aircraft_id) || null;
  }

  // ── TAB 1: Flight (airports) ──────────────────────────

  const airportSection = document.createElement('div');
  airportSection.className = 'flight-log-modal__airports';
  const airportGrid = document.createElement('div');
  airportGrid.className = 'form-grid';

  const depContainer = document.createElement('div');
  depContainer.className = 'form-grid__field';
  depContainer.appendChild(createAirportComboBox({
    name: 'flight-departure',
    label: 'Departure',
    aircraftId: currentAircraft?.id || null,
    value: flight?.departure || lastDep,
    airports: airportsList,
    favourites: [],
    onSelect: (ap) => { selectedDeparture = ap.icao_code || ap.name || ''; },
  }));
  airportGrid.appendChild(depContainer);

  const destContainer = document.createElement('div');
  destContainer.className = 'form-grid__field';
  destContainer.appendChild(createAirportComboBox({
    name: 'flight-destination',
    label: 'Destination',
    aircraftId: currentAircraft?.id || null,
    value: flight?.destination || lastDest,
    airports: airportsList,
    favourites: [],
    onSelect: (ap) => { selectedDestination = ap.icao_code || ap.name || ''; },
  }));
  airportGrid.appendChild(destContainer);

  airportSection.appendChild(airportGrid);

  // ── "Log as" alias dropdown (shown only if user has aliases) ──
  let logAsSection = null;
  if (userAliases.length > 0) {
    logAsSection = document.createElement('div');
    logAsSection.className = 'flight-log-modal__log-as';

    const logAsGrid = document.createElement('div');
    logAsGrid.className = 'form-grid';

    const logAsField = document.createElement('div');
    logAsField.className = 'form-grid__field';

    const logAsLabel = document.createElement('label');
    logAsLabel.setAttribute('for', 'flight-log-as');
    logAsLabel.textContent = 'Log as';
    logAsField.appendChild(logAsLabel);

    const logAsSelect = document.createElement('select');
    logAsSelect.id = 'flight-log-as';
    logAsSelect.name = 'flight-log-as';
    logAsSelect.className = 'form-field';

    // Default option — log as yourself
    const defaultOpt = document.createElement('option');
    defaultOpt.value = '';
    defaultOpt.textContent = '— Myself —';
    logAsSelect.appendChild(defaultOpt);

    for (const alias of userAliases) {
      const opt = document.createElement('option');
      opt.value = alias.id;
      opt.textContent = alias.name + (alias.description ? ` (${alias.description})` : '');
      logAsSelect.appendChild(opt);
    }

    logAsSelect.addEventListener('change', () => {
      selectedAliasId = logAsSelect.value || null;
    });

    logAsField.appendChild(logAsSelect);
    logAsGrid.appendChild(logAsField);
    logAsSection.appendChild(logAsGrid);
    airportSection.appendChild(logAsSection);
  }

  // ── TAB 2: Fuel & Oil + Fuel Account ──────────────────

  const fuelPane = document.createElement('div');

  // "Tank Levels" subheading
  const tankHeading = document.createElement('h4');
  tankHeading.textContent = 'Tank Levels';
  tankHeading.style.margin = '0 0 0.5rem';
  fuelPane.appendChild(tankHeading);

  const fuelContainer = document.createElement('div');
  fuelContainer.className = 'flight-log-modal__fuel-container';

  function renderFuelSection(aircraft, existingFuelOil = null) {
    fuelContainer.innerHTML = '';
    if (aircraft) {
      fuelContainer.appendChild(buildFuelFields(
        aircraft.fuel_tank_config || null,
        null,
        existingFuelOil,
      ));
    } else {
      const msg = document.createElement('p');
      msg.className = 'text-muted';
      msg.textContent = 'Select an aircraft to see fuel tank fields.';
      fuelContainer.appendChild(msg);
    }
  }

  renderFuelSection(currentAircraft, fuelOil);
  fuelPane.appendChild(fuelContainer);

  // Fuel account form
  const fuelAccountEl = createFuelAccountForm({
    initialType: flight?.fuel_account_type || undefined,
  });
  fuelPane.appendChild(fuelAccountEl);

  // ── TAB 3: Defects ─────────────────────────────────────

  const defectsPane = document.createElement('div');

  const existingDefectDescs = (defects || []).map(d => d.description || '');
  const defectListEl = createDefectList({ initialDefects: existingDefectDescs });
  defectsPane.appendChild(defectListEl);

  // ── TAB 4: Costs ──────────────────────────────────────

  const costsPane = document.createElement('div');

  const expenseHeading = document.createElement('h4');
  expenseHeading.textContent = 'Expenses';
  expenseHeading.style.margin = '0 0 0.5rem';
  costsPane.appendChild(expenseHeading);

  const expenseListEl = createExpenseList({ initialExpenses: expenses });
  costsPane.appendChild(expenseListEl);

  // ── TAB 5: Passengers ─────────────────────────────────

  const passengersPane = document.createElement('div');

  const existingPassengers = (isEdit && flight.meta?.passengers) || [];
  const passengerListEl = createPassengerList({ initialPassengers: existingPassengers });
  passengersPane.appendChild(passengerListEl);

  // ── TAB 6: Notes ──────────────────────────────────────

  const notesPane = document.createElement('div');

  const notesLabel = document.createElement('label');
  notesLabel.setAttribute('for', 'flight-notes');
  notesLabel.textContent = 'Flight Notes';
  notesLabel.style.display = 'block';
  notesLabel.style.margin = '0 0 0.5rem';
  notesLabel.style.fontWeight = '600';
  notesPane.appendChild(notesLabel);

  const notesTextarea = document.createElement('textarea');
  notesTextarea.id = 'flight-notes';
  notesTextarea.name = 'flight-notes';
  notesTextarea.rows = 6;
  notesTextarea.placeholder = 'Any notes about this flight...';
  notesTextarea.style.width = '100%';
  notesTextarea.style.resize = 'vertical';
  if (isEdit && flight.notes) {
    notesTextarea.value = flight.notes;
  } else if (isEdit && flight.purpose) {
    notesTextarea.value = `Purpose: ${flight.purpose}`;
  }
  notesPane.appendChild(notesTextarea);

  // ── Build tabs ────────────────────────────────────────

  const { wrapper: tabsWrapper } = buildTabs([
    { id: 'flight', label: 'Flight', content: airportSection },
    { id: 'fuel', label: 'Fuel & Oil', content: fuelPane },
    { id: 'defects', label: 'Defects', content: defectsPane },
    { id: 'costs', label: 'Costs', content: costsPane },
    { id: 'passengers', label: 'Passengers', content: passengersPane },
    { id: 'notes', label: 'Notes', content: notesPane },
  ]);

  // Title
  const title = isEdit ? `Log Flight - ${flight.flight_date || ''}` : 'Log Flight';

  // Open the modal
  openDetailModal({
    title,
    fields,
    values,
    customSections: [tabsWrapper],
    onSave: async (formData) => {
      const aircraftId = formData['flight-aircraft'];
      const pilotId = selectedAliasId || formData['flight-pilot'] || user?.id;

      // Block save if the selected aircraft has no fuel tanks configured
      const selectedAc = aircraftList.find(a => a.id === aircraftId);
      const fuelTanks = getFuelTanks(selectedAc?.fuel_tank_config);
      if (fuelTanks.length === 0) {
        throw new Error(
          'This aircraft has no fuel tanks configured. Please ask an admin to add fuel tank details in Aircraft settings before logging a flight.',
        );
      }

      const depInput = document.querySelector('#airport-cb-flight-departure');
      const destInput = document.querySelector('#airport-cb-flight-destination');
      const departure = depInput?.value?.trim() || selectedDeparture || '';
      const destination = destInput?.value?.trim() || selectedDestination || '';

      await handleFreeTextAirport(departure, airportsList);
      await handleFreeTextAirport(destination, airportsList);

      // Time validation: arrival > departure (same day), block >= flight
      const depT = formData['flight-dep-time'];
      const arrT = formData['flight-arr-time'];
      if (depT && arrT) {
        const [dh, dm] = depT.split(':').map(Number);
        const [ah, am] = arrT.split(':').map(Number);
        if (ah * 60 + am <= dh * 60 + dm) {
          throw new Error('Arrival time must be after departure time.');
        }
      }
      const blockMins = hmmToMins(formData['flight-block']);
      const flightMins = hmmToMins(formData['flight-time']);
      if (blockMins && flightMins && blockMins <= flightMins) {
        throw new Error('Block time must be greater than Flight time (block = flight + taxi).');
      }

      const flightPayload = {
        aircraft_id: aircraftId,
        pilot_id: pilotId,
        captain_name: formData['flight-captain'] || undefined,
        flight_date: formData['flight-date'],
        departure: departure || undefined,
        destination: destination || undefined,
        departure_time: formData['flight-dep-time'] || undefined,
        arrival_time: formData['flight-arr-time'] || undefined,
        block_time_mins: hmmToMins(formData['flight-block']),
        flight_time_mins: hmmToMins(formData['flight-time']),
        ifr_time_mins: hmmToMins(formData['flight-ifr'] || '0:00'),
        takeoffs: parseInt(formData['flight-takeoffs'], 10) || 1,
        landings: parseInt(formData['flight-landings'], 10) || 1,
        who_pays: formData['flight-who-pays'] || 'CAPTAIN',
        notes: notesTextarea.value.trim() || undefined,
        meta: {},
      };

      // Passengers
      const passengers = passengerListEl.getPassengers ? passengerListEl.getPassengers() : [];
      if (passengers.length > 0) {
        flightPayload.meta.passengers = passengers;
      }

      // If completing an existing booking, pass its id
      if (isEdit && flight.id) {
        flightPayload.id = flight.id;
      }

      const fuelOilPayload = collectFuelOilData(fuelContainer);

      // Merge fuel account data
      if (fuelAccountEl.getPayload) {
        const fa = fuelAccountEl.getPayload();
        if (fa && fa.type) {
          fuelOilPayload.fuel_account_type = fa.type;
          if (fa.litres > 0) fuelOilPayload.fuel_uplift_litres = fa.litres;
          if (fa.cost > 0 || fa.totalCost > 0) fuelOilPayload.fuel_uplift_cost = fa.totalCost || fa.cost;
          if (fa.currency) fuelOilPayload.fuel_uplift_cost_currency = fa.currency;
        }
      }

      const defectDescs = defectListEl.getDefects ? defectListEl.getDefects() : [];
      const defectPayloads = defectDescs.map(desc => ({
        aircraft_id: aircraftId,
        pilot_id: pilotId,
        description: desc,
        status: 'OPEN',
        opened_at: new Date().toISOString(),
      }));
      const expensePayloads = expenseListEl.getExpenses ? expenseListEl.getExpenses() : [];

      const result = await logFlightWithFuelAndDefects({
        flight: flightPayload,
        fuelOil: Object.keys(fuelOilPayload).length > 0 ? fuelOilPayload : null,
        defects: defectPayloads,
        expenses: expensePayloads,
      });

      if (result.error) {
        throw new Error(result.error);
      }

      // Store last-used airports in sessionStorage for pre-populating new flights
      if (departure) sessionStorage.setItem('lastDeparture', departure);
      if (destination) sessionStorage.setItem('lastDestination', destination);

      if (onSaved) onSaved();
    },
  });

  // Wire up after modal is in the DOM
  requestAnimationFrame(() => {
    // Add scoping class for flight-log-specific CSS overrides
    const modalEl = document.querySelector('.detail-modal');
    if (modalEl) {
      modalEl.classList.add('detail-modal--flight-log');
    }

    // ── No-tank-config banner + save gate ────────────────
    // If the selected aircraft has no fuel tanks configured, show a
    // prominent banner at the top of the modal body and disable Save
    // so the user isn't frustrated after filling in all the fields.
    const modalBody = document.querySelector('.detail-modal__body');
    let noTankBanner = null;

    function checkAircraftTankConfig(aircraft) {
      const saveBtn = document.querySelector('.detail-modal__btn--save');
      const fuelTanks = getFuelTanks(aircraft?.fuel_tank_config);
      const hasFuelTanks = fuelTanks.length > 0;

      if (!hasFuelTanks && aircraft) {
        // Show banner if not already present
        if (!noTankBanner) {
          noTankBanner = document.createElement('div');
          noTankBanner.className = 'flight-log-modal__no-tank-banner';
          noTankBanner.setAttribute('role', 'alert');
          noTankBanner.innerHTML =
            '<strong>⚠️ Cannot log flight</strong> — this aircraft has no fuel tanks configured. ' +
            'Please ask an admin to add fuel tank details in Aircraft settings.';
          if (modalBody) modalBody.prepend(noTankBanner);
        }
        if (saveBtn) {
          saveBtn.disabled = true;
          saveBtn.title = 'Aircraft fuel tanks must be configured before logging a flight';
        }
      } else {
        // Remove banner and re-enable save
        if (noTankBanner) {
          noTankBanner.remove();
          noTankBanner = null;
        }
        if (saveBtn) {
          saveBtn.disabled = false;
          saveBtn.title = '';
        }
      }
    }

    // Check on initial load
    checkAircraftTankConfig(currentAircraft);

    // Aircraft change → re-render fuel section + check tank config
    const aircraftSelect = document.getElementById('flight-aircraft');
    if (aircraftSelect) {
      aircraftSelect.addEventListener('change', () => {
        const selected = aircraftList.find(a => a.id === aircraftSelect.value) || null;
        currentAircraft = selected;
        renderFuelSection(selected);
        checkAircraftTankConfig(selected);
      });
    }

    // Time conversion hints for block/flight/IFR fields
    wireTimeConversion('flight-block');
    wireTimeConversion('flight-time');
    wireTimeConversion('flight-ifr');

    // Auto-calc Flight + Block time from departure/arrival
    const depEl = document.querySelector('[name="flight-dep-time"]');
    const arrEl = document.querySelector('[name="flight-arr-time"]');
    const blockEl = document.querySelector('[name="flight-block"]');
    const flightEl = document.querySelector('[name="flight-time"]');
    const recalc = () => {
      if (!depEl?.value || !arrEl?.value) return;
      const [dh, dm] = depEl.value.split(':').map(Number);
      const [ah, am] = arrEl.value.split(':').map(Number);
      const mins = (ah * 60 + am) - (dh * 60 + dm);
      if (mins <= 0) return;
      if (flightEl) flightEl.value = minsToHMM(mins);
      // TODO(Phase 4): make taxi padding a user setting (default 5 min)
      if (blockEl) blockEl.value = minsToHMM(mins + 5);
      blockEl?.dispatchEvent(new Event('input', { bubbles: true }));
      flightEl?.dispatchEvent(new Event('input', { bubbles: true }));
    };
    // Departure chosen → suggest arrival = dep + 1hr and focus arrival for editing
    depEl?.addEventListener('change', () => {
      if (!depEl.value) return;
      if (!arrEl) return;
      if (!arrEl.value) {
        const [dh, dm] = depEl.value.split(':').map(Number);
        const total = (dh * 60 + dm + 60) % (24 * 60);
        const hh = String(Math.floor(total / 60)).padStart(2, '0');
        const mm = String(total % 60).padStart(2, '0');
        arrEl.value = `${hh}:${mm}`;
      }
      arrEl.focus();
      arrEl.select?.();
      recalc();
    });
    arrEl?.addEventListener('change', recalc);
  });
}

// ── Time conversion hint ─────────────────────────────────

/**
 * Attach a live conversion hint next to a time input field.
 * Shows decimal when user types h:mm, shows h:mm when user types decimal.
 * @param {string} fieldId - The input element id
 */
function wireTimeConversion(fieldId) {
  const input = document.getElementById(fieldId);
  if (!input) return;

  const hint = document.createElement('span');
  hint.className = 'flm-time-hint';
  input.parentElement.appendChild(hint);

  function update() {
    const val = input.value.trim();
    if (!val) { hint.textContent = ''; return; }
    const mins = hmmToMins(val);
    if (mins <= 0) { hint.textContent = ''; return; }
    // Show the opposite format
    if (val.includes('.')) {
      hint.textContent = `= ${minsToHMM(mins)}`;
    } else {
      hint.textContent = `= ${minsToDecimal(mins)} hrs`;
    }
  }

  input.addEventListener('input', update);
  update(); // initial if pre-populated
}

// ── Internal helpers ─────────────────────────────────────

/**
 * Map of recognised tank names (case-insensitive) to flight_fuel_oil columns.
 * Any tank whose name doesn't match is stored in meta.tank_readings instead,
 * so the Supabase insert never sends unknown column names.
 */
const TANK_NAME_TO_COLUMN = {
  'main':       'main_tank_level',
  'main tank':  'main_tank_level',
  'left wing':  'left_wing_level',
  'right wing': 'right_wing_level',
  'belly':      'belly_tank_level',
  'belly tank': 'belly_tank_level',
};

function collectFuelOilData(fuelContainer) {
  const payload = {};
  const tankReadings = {};
  const fuelInputs = fuelContainer.querySelectorAll('.flight-log-modal__fuel-input');

  for (const input of fuelInputs) {
    const idx = input.getAttribute('data-tank-index');
    const name = (input.getAttribute('data-tank-name') || '').trim();
    if (!input.value) continue;

    let numericValue = parseFloat(input.value);
    if (Number.isNaN(numericValue)) continue;

    // Clamp: no negatives, respect max capacity if set
    if (numericValue < 0) numericValue = 0;
    const max = parseFloat(input.max);
    if (!Number.isNaN(max) && max > 0 && numericValue > max) numericValue = max;

    const column = TANK_NAME_TO_COLUMN[name.toLowerCase()];

    if (column) {
      // Known column — store the numeric reading directly
      payload[column] = numericValue;
    }

    // Always store in meta.tank_readings keyed by index and name for
    // round-trip fidelity (the buildFuelFields pre-populate reads these)
    tankReadings[`tank_${idx}`] = numericValue;
    if (name) tankReadings[name] = numericValue;
  }

  // Stash dynamic readings in the meta JSONB column so nothing is lost
  if (Object.keys(tankReadings).length > 0) {
    payload.meta = { tank_readings: tankReadings };
  }

  return payload;
}

async function handleFreeTextAirport(code, airportsList) {
  if (!code) return;
  const exists = airportsList.some(
    a => a.icao_code?.toLowerCase() === code.toLowerCase() || a.name?.toLowerCase() === code.toLowerCase(),
  );
  if (!exists && code.length > 0) {
    try {
      await createAirport({ icao_code: code.toUpperCase(), name: code, type: 'airstrip' });
    } catch (_) {
      // Silently ignore — flight can still reference it
    }
  }
}
